/**
 * Copyright IT People Corporation. All Rights Reserved.
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Contributors: Mohan Venkataraman, Dilip Manjunatha, Aashish Shrestha, Sahinul Haque
 */

import {
  API_ENDPOINT_GETUSER_ITEMS, API_ENDPOINT_GETITEM_BYID, API_ENDPOINT_GETITEM_HISTORY_BYID,
  API_ENDPOINT_ADDITEM, API_ENDPOINT_ITEM_TRANSFER,
  HEADERS
} from './Constants.js';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import config from '../utils/EnvironmentSwitcher';
import Logger from '../utils/Logger';
import { fetchWithFabricRetry } from '../utils/fabricReEnroll';
export var ArtworkCancelPromise = {
  getArtworkWithId: false,
  getHistoryForArtworkWithId: false
};
// Add new function to create Stripe product
async function createStripeProduct(nftData) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('No authenticated user found');
    }

    const idToken = await user.getIdToken(true);
    Logger.info("Creating Stripe product with price in cents:", nftData);
    // Convert price to cents for Stripe
    const priceInCents = Math.round(parseFloat(nftData.default_price) * 100);
    
    Logger.info("Creating Stripe product with price in cents:", priceInCents);

    // Prepare the product data
    const productData = {
      id: nftData.id,
      name: nftData.name || "Mieros Creator Verification",
      default_price: {
        currency: "usd",
        unit_amount: priceInCents  // Make sure this is a number and in cents
      },
      description: nftData.description || "Verify your creator account on Mieros",
      images: [nftData.image || "https://your-default-image-url.com/image.jpg"],
      metadata: {
        type: "verification",
        level: "creator",
        nftId: nftData.id,
        creatorId: user.uid
      }
    };

    Logger.info('Creating Stripe product with data:', productData);

    const response = await fetch(`${config.paymentUrl}/create-mieros-product`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(productData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create Stripe product');
    }

    return await response.json();
  } catch (error) {
    Logger.error('Error creating Stripe product:', error);
    throw error;
  }
}

export default class Artwork {

  async getArtworkForCurrentUser() {
    const headers = await HEADERS();
    return new Promise(function (resolve, reject) {
      fetch(`${config.apiUrl}/nft/me`, {
        headers,
        method: 'GET',
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          return response.json().then((body) => {
            throw new Error(body?.message || body?.error || response.statusText);
          }).catch((e) => {
            if (e instanceof Error) throw e;
            throw new Error(response.statusText);
          });
        })
        .then((data) => {
          if (data && data.success === false) {
            reject(new Error(data.message || data.error || 'Failed to load artwork'));
            return;
          }
          resolve(data);
        })
        .catch((err) => {
          toast.dismiss();
          toast.error(err?.message || err);
          reject(err);
        });
    });
  }

  async getArtworkWithId(nftID) {
    ArtworkCancelPromise.getArtworkWithId = false;
    const headers = await HEADERS();
    return new Promise(function (resolve, reject) {
      fetch(`${config.apiUrl}/nft/${nftID}`, {
        headers,
        method: 'GET',
      }).then(response => {
        if (response.status === 200) {
          return response.json();
        }
        response.json().then(err => {
          reject(err.message);
        }).catch(e => {
          reject(response.statusText);
        });
        throw new Error(response.statusText);
      })
        .then(data => {
          if (!ArtworkCancelPromise.getArtworkWithId) {
            resolve(data);
          } else {
            reject({ message: "Canceled promise." });
          }
        })
        .catch(err => {
          toast.dismiss();
          toast.error(err);
        });
    });
  }

  async getHistoryForArtworkWithId(nftID) {
    ArtworkCancelPromise.getHistoryForArtworkWithId = false;
    const headers = await HEADERS();
    return new Promise(function (resolve, reject) {
      fetch(`${config.apiUrl}/nft/${nftID}/history`, {
        headers,
        method: 'GET',
      }).then(response => {
        if (response.status === 200) {
          return response.json();
        }
        response.json().then(err => {
          reject(err.message);
        }).catch(e => {
          reject(response.statusText);
        });
        throw new Error(response.statusText);
      })
        .then(data => {
          if (!ArtworkCancelPromise.getHistoryForArtworkWithId) {
            resolve(data);
          } else {
            reject({ message: "Canceled promise." });
          }
        })
        .catch(err => {
          toast.dismiss();
          toast.error(err);
        });
    });
  }

  async createArtwork(formData) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        Logger.error('No authenticated user found');
        throw new Error('No authenticated user found');
      }

      try {
        const idToken = await user.getIdToken(true);
        Logger.debug('Got fresh token:', idToken ? 'Token present' : 'No token');

        // Convert main file to base64
        const file = formData.get('file');
        if (!file) {
          throw new Error('Main file is required');
        }

        const base64Image = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
          };
          reader.readAsDataURL(file);
        });

        // Handle itemAsset - could be a File object or a string
        const itemAsset = formData.get('itemAsset');
        let base64ItemAsset = '';
        let itemAssetType = '';
        let itemAssetName = '';

        if (itemAsset) {
          if (itemAsset instanceof Blob || itemAsset instanceof File) {
            // If it's a file/blob, convert to base64
            base64ItemAsset = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
              };
              reader.readAsDataURL(itemAsset);
            });
            itemAssetType = itemAsset.type || 'application/octet-stream';
            itemAssetName = itemAsset.name || 'asset';
          } else if (typeof itemAsset === 'string') {
            // If it's already a string (possibly base64), use it directly
            base64ItemAsset = itemAsset.includes('base64,') 
              ? itemAsset.split('base64,')[1] 
              : itemAsset;
            itemAssetType = 'application/octet-stream';
            itemAssetName = 'asset';
          } else {
            Logger.error('Invalid itemAsset type:', typeof itemAsset);
            throw new Error('Invalid itemAsset format');
          }
        } else {
          // When no separate asset file is uploaded (e.g. Art/Music), reuse the preview file as the deliverable asset.
          base64ItemAsset = base64Image;
          itemAssetType = file.type || 'image/png';
          itemAssetName = file.name || 'asset';
        }

        // Build JSON payload
        const jsonPayload = {
          userId: user.uid,
          itemImage: base64Image,
          itemImageType: file.type || 'image/png',
          itemAsset: base64ItemAsset,
          itemAssetType: itemAssetType,
          itemAssetName: itemAssetName
        };

        // Add other form fields
        formData.forEach((value, key) => {
          if (key !== 'file' && key !== 'itemAsset') {
            jsonPayload[key] = value;
          }
        });

        Logger.debug('Making request with payload:', {
          ...jsonPayload,
          itemImage: 'BASE64_DATA_OMITTED',
          itemAsset: 'BASE64_DATA_OMITTED'
        });

        // fetchWithFabricRetry: on 403 FABRIC_ACCESS_DENIED, ensures ledger onboarding and retries once
        const response = await fetchWithFabricRetry(`${config.apiUrl}/nft/mint`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(jsonPayload)
        });

        if (!response.ok) {
          const errorText = await response.text();
          try {
            const errorData = JSON.parse(errorText);
            throw new Error(errorData.message || 'Failed to mint NFT');
          } catch (e) {
            throw new Error(`Server error: ${errorText}`);
          }
        }

        const data = await response.json();
        Logger.info("NFT minted successfully:", data);

        const usdPrice = Number(jsonPayload.usdPrice || 0);
        const wantsStripe =
          jsonPayload.createStripeProduct === true ||
          jsonPayload.createStripeProduct === 'true';
        const shouldCreateStripeProduct = wantsStripe && Number.isFinite(usdPrice) && usdPrice > 0;

        // Only create Stripe product when USD price is set and > 0.
        if (!shouldCreateStripeProduct) {
          return {
            nft: data,
            stripeSkipped: true
          };
        }

        try {
          const stripeProduct = await createStripeProduct({
            name: data.itemDetail,
            default_price: usdPrice,
            description: data.itemDescription,
            image: data.imageUrl,
            id: data.nftId
          });

          return {
            nft: data,
            stripeProduct: stripeProduct
          };
        } catch (stripeError) {
          Logger.error('Error creating Stripe product:', stripeError);
          return {
            nft: data,
            stripeError: stripeError.message
          };
        }

      } catch (error) {
        Logger.error('Error in NFT creation:', error);
        throw error;
      }

    } catch (error) {
      Logger.error('Error in createArtwork:', error);
      if (error.message.includes('Session expired') ||
        error.message.includes('No authenticated user found')) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async transferArtworkToUser(artwork) {
    const headers = await HEADERS();
    return new Promise(function (resolve, reject) {
        fetchWithFabricRetry(`${config.apiUrl}/nft/transfer`, {
        headers,
        method: 'POST',
        body: JSON.stringify(artwork),
      }).then(response => {
        if (response.status === 200) {
          return response.json();
        }
        response.json().then(err => {
          reject(err.message);
        }).catch(e => {
          reject(response.statusText);
        });
        throw new Error(response.statusText);
      })
        .then(data => resolve(data))
        .catch(err => {
          toast.dismiss();
          toast.error(err);
        });
    });
  }

}
